import type { TLRecord, TLShape } from "@tldraw/tlschema";
import type { ChangeFact, ChangeBounds } from "./changes";
import { plainText } from "./updates";
import { distance, formatLength } from "./geometry";
type Lookup = (id: string) => TLRecord | undefined;
function text(value: unknown) {
  return typeof value === "string" ? value.slice(0, 256) : "";
}
// A bounded fingerprint for presentation matching, not an authorization primitive.
export function changeSignature(record: TLRecord) {
  let content: unknown;
  if (record.typeName === "shape")
    content = [
      record.parentId,
      record.x,
      record.y,
      record.rotation,
      record.index,
      record.opacity,
      record.isLocked,
      record.props,
      record.meta.metersPerUnit,
      record.meta.paperOpacity,
    ];
  else if (record.typeName === "comment")
    content = [record.body, record.isDeleted];
  else if (record.typeName === "comment-thread")
    content = [record.anchor, record.resolved, record.isDeleted];
  else return "";
  const encoded = JSON.stringify(content);
  let a = 2166136261,
    b = 3339675911;
  for (let i = 0; i < encoded.length; i++) {
    const c = encoded.charCodeAt(i);
    a = Math.imul(a ^ c, 16777619);
    b = Math.imul(b ^ c, 2246822519);
  }
  return `${a >>> 0}:${b >>> 0}`;
}
function localBounds(shape: TLShape): ChangeBounds {
  const p: Record<string, unknown> = { ...shape.props };
  let points: { x: number; y: number }[] = [];
  if (Array.isArray(p.points)) points = p.points;
  else if (p.points && typeof p.points === "object")
    points = Object.values(p.points);
  else if (Array.isArray(p.segments))
    points = p.segments.flatMap((s) => s.points ?? []);
  const valid = points.filter(
    (pt) => Number.isFinite(pt?.x) && Number.isFinite(pt?.y),
  );
  if (valid.length) {
    let x = Infinity,
      y = Infinity,
      right = -Infinity,
      bottom = -Infinity;
    for (const pt of valid) {
      x = Math.min(x, pt.x);
      y = Math.min(y, pt.y);
      right = Math.max(right, pt.x);
      bottom = Math.max(bottom, pt.y);
    }
    return { x, y, w: Math.max(1, right - x), h: Math.max(1, bottom - y) };
  }
  return {
    x: 0,
    y: 0,
    w: typeof p.w === "number" ? p.w : 160,
    h: typeof p.h === "number" ? p.h : 60,
  };
}
function shapeContext(shape: TLShape, get: Lookup) {
  const box = localBounds(shape);
  let corners = [
    { x: box.x, y: box.y },
    { x: box.x + box.w, y: box.y },
    { x: box.x, y: box.y + box.h },
    { x: box.x + box.w, y: box.y + box.h },
  ];
  let current: TLRecord | undefined = shape,
    scope = "",
    scopeName = "Canvas",
    pageId = "";
  for (let depth = 0; current?.typeName === "shape" && depth < 32; depth++) {
    if (current.type === "sheet" || current.type === "trace") {
      scope = current.id;
      scopeName = text(current.props.name) || "Sheet";
    }
    const { rotation, x, y } = current,
      cos = Math.cos(rotation),
      sin = Math.sin(rotation);
    corners = corners.map((p) => ({
      x: x + p.x * cos - p.y * sin,
      y: y + p.x * sin + p.y * cos,
    }));
    if (current.parentId.startsWith("page:")) pageId = current.parentId;
    current = get(current.parentId);
  }
  const x = Math.min(...corners.map((p) => p.x)),
    y = Math.min(...corners.map((p) => p.y));
  const bounds = {
    x,
    y,
    w: Math.max(1, Math.max(...corners.map((p) => p.x)) - x),
    h: Math.max(1, Math.max(...corners.map((p) => p.y)) - y),
  };
  return {
    pageId,
    scope: scope || `${pageId}:${Math.floor(x / 800)}:${Math.floor(y / 800)}`,
    scopeName,
    bounds,
  };
}
// Only display measured values when the same source rules can establish scale.
function measuredText(shape: TLShape, get: Lookup) {
  if (
    shape.type !== "mark" ||
    shape.props.kind !== "dimension" ||
    shape.props.points.length < 2
  )
    return "";
  let current = get(shape.parentId),
    scale = 0;
  for (let depth = 0; current?.typeName === "shape" && depth < 32; depth++) {
    if (current.type === "sheet") {
      scale = current.props.metersPerUnit;
      break;
    }
    if (current.type === "image") {
      if (!current.meta.scaleReview)
        scale =
          (Number(current.meta.metersPerUnit ?? 0) *
            Number(current.meta.calibrationWidth ?? current.props.w)) /
          current.props.w;
      break;
    }
    if (current.type === "trace" && current.parentId.startsWith("page:")) {
      if (!current.meta.scaleReview)
        scale = Number(current.meta.metersPerUnit ?? 0);
      break;
    }
    current = get(current.parentId);
  }
  return Number.isFinite(scale) && scale > 0
    ? formatLength(
        distance(shape.props.points[0], shape.props.points[1]) * scale,
      )
    : "";
}
export function changeFact(
  record: TLRecord | undefined,
  get: Lookup,
): ChangeFact | null {
  if (!record) return null;
  if (record.typeName === "shape") {
    const p: Record<string, unknown> = { ...record.props };
    const type =
      record.type === "mark" || record.type === "drafting"
        ? text(p.kind)
        : ({
            draw: "sketch",
            image: "image",
            geo: "shape",
            note: "note",
            trace: "tracing sheet",
            document: "document",
            table: "table",
            scene: "3D model",
            sheet: "sheet",
            text: "text block",
            line: "line",
            arrow: "arrow",
            highlight: "highlight",
            bookmark: "link",
            embed: "embed",
            frame: "frame",
            group: "group",
            video: "video",
          }[record.type] ?? "object");
    const content =
      measuredText(record, get) ||
      text(p.text) ||
      plainText(p.richText).slice(0, 256);
    return {
      id: record.id,
      type,
      label: text(p.name) || content || type,
      ...shapeContext(record, get),
      signature: changeSignature(record),
      ...(content ? { text: content } : {}),
    };
  }
  if (record.typeName === "comment" || record.typeName === "comment-thread") {
    if (record.isDeleted) return null;
    const thread =
      record.typeName === "comment-thread" ? record : get(record.threadId);
    if (thread?.typeName !== "comment-thread") return null;
    const anchor = thread.anchor;
    const target = anchor.type === "shape" ? get(anchor.shapeId) : undefined;
    const context =
      target?.typeName === "shape"
        ? shapeContext(target, get)
        : {
            pageId: thread.pageId,
            scope: thread.id,
            scopeName: "Discussion",
            bounds: {
              x: "x" in anchor ? Number(anchor.x) : 0,
              y: "y" in anchor ? Number(anchor.y) : 0,
              w: "w" in anchor ? Number(anchor.w) : 48,
              h: "h" in anchor ? Number(anchor.h) : 48,
            },
          };
    const content =
      record.typeName === "comment"
        ? plainText(record.body).slice(0, 256)
        : thread.resolved
          ? "Resolved discussion"
          : "Open discussion";
    return {
      id: record.id,
      type: record.typeName === "comment" ? "comment" : "discussion",
      label: content,
      ...context,
      threadId: thread.id,
      signature: changeSignature(record),
      text: content,
    };
  }
  return null;
}
